import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Virtualizer, type VirtualizerHandle } from 'virtua';
import { SenderSource } from '@enuan/chat__utils';
import {
	getLastRenderableMessage,
	getMessagesBlocksProps,
} from '../../../../reduxStore/selectors/messages.selector';
import { useMessagesListRefs } from '../messagesListRefs.context';
import {
	useFirstUnreadMessageIndex,
	useLoadHistory,
	useRenderMessageItem,
} from './hooks';
import {
	LOAD_HISTORY_THRESHOLD,
	STICK_TO_BOTTOM_THRESHOLD,
	VIRTUAL_ITEM_SIZE,
} from './utils';
import { styled } from '@mui/material';

/**
 * Renders the messages list using the virtualised renderer.
 *
 * Owns all virtualisation-specific state (refs, history loading, stick-to-bottom
 * behaviour, keep-mounted window, and programmatic scrolling) so this logic is
 * only instantiated when the virtualised renderer is active.
 */
export const VirtualizedMessages = styled(
	({ className }: { className?: string }) => {
		const messagesBlockProps = useSelector(getMessagesBlocksProps);
		const renderItem = useRenderMessageItem();
		const firstUnreadMessageIndex = useFirstUnreadMessageIndex();
		const { load: loadHistory } = useLoadHistory();

		const { scrollContainerRef } = useMessagesListRefs();
		const virtualizerRef = useRef<VirtualizerHandle>(null);

		const lastMessage = useSelector(getLastRenderableMessage);
		const prevLastClientIdRef = useRef(lastMessage?.client_id);
		const isPrepend = useRef(false);
		const shouldStickToBottom = useRef(true);

		/**
		 * Indexes that must stay mounted regardless of the visible range: from the
		 * first unread message (or the last message when all are read) to the end,
		 * so the unread separator and latest messages remain measurable.
		 */
		const keepMountedIndexes = useMemo(() => {
			if (messagesBlockProps.length === 0) return [];
			const startIndex =
				firstUnreadMessageIndex >= 0
					? firstUnreadMessageIndex
					: Math.max(messagesBlockProps.length - 1, 0);
			return Array.from(
				{ length: messagesBlockProps.length - startIndex },
				(_, offset) => startIndex + offset,
			);
		}, [firstUnreadMessageIndex, messagesBlockProps.length]);

		/**
		 * Tracks whether the list is stuck to the bottom and triggers history loading
		 * when the user scrolls close to the top. Sets `isPrepend` before delegating
		 * to `loadHistory` so the virtualizer keeps the scroll position anchored
		 * during the subsequent prepend render.
		 *
		 * @param offset Current vertical scroll offset reported by the virtualizer.
		 */
		const handleScroll = useCallback(
			(offset: number) => {
				if (!virtualizerRef.current) return;
				const { scrollSize, viewportSize } = virtualizerRef.current;
				const distanceFromBottom = scrollSize - offset - viewportSize;
				shouldStickToBottom.current =
					distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD;
				if (offset < LOAD_HISTORY_THRESHOLD) {
					isPrepend.current = true;
					loadHistory();
				}
			},
			[loadHistory],
		);

		// Reset the prepend flag after every committed layout so it only affects the
		// single render that follows a history prepend.
		useLayoutEffect(() => {
			isPrepend.current = false;
		});

		// Keep the viewport pinned to the bottom when new content arrives and the
		// user is already at the bottom of the conversation.
		useEffect(() => {
			if (!virtualizerRef.current || messagesBlockProps.length === 0) return;
			if (shouldStickToBottom.current)
				virtualizerRef.current.scrollToIndex(messagesBlockProps.length - 1, {
					align: 'end',
				});
		}, [messagesBlockProps]);

		// Always scroll to the bottom when the local user sends a new message,
		// regardless of the current scroll position.
		useEffect(() => {
			if (!virtualizerRef.current || !lastMessage) return;
			if (messagesBlockProps.length === 0) return;
			const isNewMessage =
				lastMessage.client_id !== prevLastClientIdRef.current;
			prevLastClientIdRef.current = lastMessage.client_id;
			if (isNewMessage && lastMessage.sender === SenderSource.USER)
				virtualizerRef.current.scrollToIndex(messagesBlockProps.length - 1, {
					align: 'end',
				});
		}, [lastMessage, messagesBlockProps.length]);

		const children = useMemo(
			() => messagesBlockProps.map(renderItem),
			[messagesBlockProps, renderItem],
		);

		return (
			<div ref={scrollContainerRef} className={className}>
				<Virtualizer
					ref={virtualizerRef}
					scrollRef={scrollContainerRef}
					shift={isPrepend.current}
					itemSize={VIRTUAL_ITEM_SIZE}
					keepMounted={keepMountedIndexes}
					onScroll={handleScroll}
				>
					{children}
				</Virtualizer>
			</div>
		);
	},
	{ target: 'VirtualizedMessages-root', label: 'VirtualizedMessages' },
)({
	height: '100%',
	width: '100%',
	overflow: 'auto',
	overflowAnchor: 'none',
});
